import { authorizedFetch, ErrorResponse } from './api-client';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface Patient {
  id: string;
  name: string;
  cpf: string;
  birth_date: string;
  email: string;
  cellphone: string;
  postcode: string;
  city_name: string;
  state_code: string;
  card_number?: string;
  dental_insurance_id?: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  status: number;
  last_registry_review_at?: string | null;
  last_registry_review_by?: string | null;
}

export interface RegistryReviewConfig {
  days_interval: number;
}

export async function fetchRegistryReviewConfig(): Promise<RegistryReviewConfig> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/registry-review-config`);
  if (!response.ok) {
    throw new Error('Erro ao buscar configuração de revisão de cadastro');
  }
  return response.json();
}

export async function confirmRegistryReview(patientId: string): Promise<void> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/patients/${patientId}/confirm-registry-review`,
    { method: 'POST' },
  );
  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao confirmar revisão de cadastro');
  }
}

export async function fetchPatientById(id: string, complete = true): Promise<Patient> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/${id}?complete=${complete}`);

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao buscar dados do paciente');
  }

  return response.json();
}

export async function fetchPatients(
  includeInactive = false,
  complete = false,
  searchTerm?: string,
  page = 1,
  pageSize = 20,
  nameLike?: string,
  cpf?: string,
  birthDate?: string,
  email?: string,
  cellPhoneLike?: string,
  clinicId?: string
): Promise<Patient[]> {
  const params = new URLSearchParams({
    include_inactive: includeInactive.toString(),
    complete: complete.toString(),
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (searchTerm) {
    const isNumeric = /^\d+$/.test(searchTerm.replace(/\D/g, ''));
    if (isNumeric) {
      params.append('cpf', searchTerm.replace(/\D/g, ''));
    } else {
      params.append('name_like', searchTerm);
    }
  }

  if (nameLike) params.append('name_like', nameLike);
  if (cpf) params.append('cpf', cpf);
  if (birthDate) params.append('birth_date', birthDate);
  if (email) params.append('email', email);
  if (cellPhoneLike) params.append('cellphone_like', cellPhoneLike);
  if (clinicId) params.append('clinic_id', clinicId);

  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/?${params.toString()}`);

  if (!response.ok) {
    throw new Error('Erro ao buscar pacientes');
  }

  const data = await response.json();
  return data.items ?? data;
}

export interface CreatePatientData {
  name: string;
  cpf: string;
  birth_date: string;
  email: string;
  cellphone: string;
  postcode: string;
  city_name: string;
  state_code: string;
  card_number?: string;
  dental_insurance_id?: string;
}

export async function createPatient(data: CreatePatientData): Promise<Patient> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao criar paciente');
  }

  return response.json();
}

export async function updatePatient(id: string, data: CreatePatientData): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao atualizar paciente');
  }
}

export async function activatePatient(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/${id}/activate`, {
    method: 'PATCH',
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao ativar paciente');
  }
}

export async function inactivatePatient(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/${id}/inactivate`, {
    method: 'PATCH',
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao inativar paciente');
  }
}

export interface PatientByPhone {
  id: string;
  name: string;
}

export async function fetchPatientsByPhone(cellphone: string, clinicId: string): Promise<PatientByPhone[]> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/phone/${cellphone}?clinic_id=${clinicId}`);
  if (!response.ok) throw new Error('Erro ao buscar pacientes pelo telefone');
  return response.json();
}

export async function fetchPatientsByPhoneComplete(cellphone: string, clinicId: string): Promise<Patient[]> {
  const response = await authorizedFetch(
    `${API_BASE_URL}/api/v1/patients/phone/${cellphone}?clinic_id=${clinicId}&complete=true`,
  );
  if (!response.ok) throw new Error('Erro ao buscar pacientes pelo telefone');
  return response.json();
}

export async function deletePatient(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/patients/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao deletar paciente');
  }
}